/**
 * Review Tab - Three-panel tag editing interface
 */

import { Box, Paper, Snackbar } from '@mui/material';
import { useState, useEffect, useMemo, useRef } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { LessonV1_SpineTagsPrediction, TagCandidate, Product, ExperimentVersion } from '../../types';
import TagsPanel from './TagsPanel';
import BipartiteChart from './BipartiteChart';
import ResizablePanels from './ResizablePanels';
import TagGenerationConfirmDialog from './TagGenerationConfirmDialog';
import StandardsPreview from './StandardsPreview';
import { useLocalStorage } from '../../hooks/useLocalStorage';
import * as tagGenerationService from '../../services/tagGeneration.service';
import { getMathExpressionsModified, mathExpressionKeys } from '../../services/mathExpressions.service';

interface ReviewTabProps {
  prediction: LessonV1_SpineTagsPrediction;
  product: Product;
  experiment: ExperimentVersion;
  onUpdate: (updatedPrediction: LessonV1_SpineTagsPrediction) => void;
  onTagGenerationStateChange?: (isGenerating: boolean) => void;
  triggerTagGeneration?: boolean;
  onDirtyStateChange?: (isDirty: boolean) => void;
  triggerSave?: boolean;
}

export default function ReviewTab({
  prediction,
  product,
  experiment,
  onUpdate: _onUpdate,
  onTagGenerationStateChange,
  triggerTagGeneration,
  onDirtyStateChange,
  triggerSave
}: ReviewTabProps) {
  const [focusedTagIndex, setFocusedTagIndex] = useState<number | null>(null);
  const [focusedStandardId, setFocusedStandardId] = useState<string | null>(null);
  const [focusedTagStandardIds, setFocusedTagStandardIds] = useState<string[]>([]);
  const [workingPrediction, setWorkingPrediction] = useState<LessonV1_SpineTagsPrediction>(prediction);
  const [isDirty, setIsDirty] = useState(false);

  // Use ref to avoid stale closure in save handler
  const workingPredictionRef = useRef(workingPrediction);
  useEffect(() => {
    workingPredictionRef.current = workingPrediction;
  }, [workingPrediction]);

  // Tag generation state
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  const queryClient = useQueryClient();

  // Fetch modified math expressions for standards preview
  // This is the same data source that MathtasticLimiterGroupAccordion uses
  const { data: modifiedMathExpressionsData } = useQuery({
    queryKey: mathExpressionKeys.modified(prediction.id),
    queryFn: () => getMathExpressionsModified(prediction.id),
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: false, // Don't retry on 404 (no modified expressions)
  });

  // Extract expressions array for passing to child components
  const modifiedMathExpressions = modifiedMathExpressionsData?.expressions;

  // Sync working prediction when the prediction prop changes (e.g., after save/refetch)
  // Always sync and clear dirty - prediction only changes after save completes or initial load
  useEffect(() => {
    setWorkingPrediction(prediction);
    setIsDirty(false);
  }, [prediction]);

  // Filter state - persisted in localStorage
  const [selectedGrades, setSelectedGrades] = useLocalStorage<string[]>('lessonDetail.filters.grades', []);
  const [selectedDocuments, setSelectedDocuments] = useLocalStorage<string[]>('lessonDetail.filters.documents', []);
  const [selectedJurisdictions, setSelectedJurisdictions] = useLocalStorage<string[]>('lessonDetail.filters.jurisdictions', []);
  const [selectedStates, setSelectedStates] = useLocalStorage<string[]>('lessonDetail.filters.states', []);
  const [showNonSelectedTags, setShowNonSelectedTags] = useLocalStorage<boolean>('lessonDetail.filters.showNonSelectedTags', false);
  const [searchText, setSearchText] = useLocalStorage<string>('lessonDetail.filters.searchText', '');

  // Handle tag selection from chart or tag list
  const handleTagFocus = (tagIndex: number | null) => {
    setFocusedTagIndex(tagIndex);
  };

  // Handle standard selection from Standards tab
  const handleStandardClick = (standardId: string | null) => {
    setFocusedStandardId(standardId);
    // Clear tag standards when directly selecting a standard
    setFocusedTagStandardIds([]);
  };

  // Handle tag updates from editor
  const handleTagUpdate = (updatedTag: TagCandidate, tagIndex: number) => {
    const newPrediction = { ...workingPrediction };
    // Create a new array to ensure React detects the change
    newPrediction.tag_prediction_modified = [
      ...(workingPrediction.tag_prediction_modified || workingPrediction.tag_prediction_original || [])
    ];
    newPrediction.tag_prediction_modified[tagIndex] = updatedTag;

    setWorkingPrediction(newPrediction);
    setIsDirty(true);
  };

  // Handle adding new tag candidates from competency search
  const handleAddTagCandidates = (newTagCandidates: TagCandidate[]) => {
    const newPrediction = { ...workingPrediction };
    const existingTagCandidates = workingPrediction.tag_prediction_modified || workingPrediction.tag_prediction_original || [];

    // Create a new array with existing + new tag candidates
    newPrediction.tag_prediction_modified = [
      ...existingTagCandidates,
      ...newTagCandidates
    ];

    setWorkingPrediction(newPrediction);
    setIsDirty(true);
  };

  // Handle revert
  const handleRevert = () => {
    setWorkingPrediction(prediction);
    setIsDirty(false);
    setFocusedTagIndex(null);
  };

  // Tag generation mutation
  // Tag generation mutation (for clean data)
  const generateTagsMutation = useMutation({
    mutationFn: () => tagGenerationService.generateTagsForLesson(product, experiment, prediction.id),
    onSuccess: (result) => {
      if (result.success) {
        // Update working prediction with new tag IDs
        const updatedPrediction = {
          ...workingPrediction,
          spine_tag_ids: result.tagIds,
        };

        // Auto-save the changes to the database instead of marking as dirty
        _onUpdate(updatedPrediction);

        // Update local state and clear dirty flag since we auto-saved
        setWorkingPrediction(updatedPrediction);
        setIsDirty(false);

        // Show success message
        const mathCount = result.mathExpressionTags?.length || 0;
        const message = `Generated and saved ${result.tagIds.length} SPINE tag(s)${mathCount > 0 ? ` including ${mathCount} math expression(s)` : ''}`;
        setSuccessMessage(message);
        setShowSuccessMessage(true);

        // Invalidate prediction queries to ensure fresh data
        queryClient.invalidateQueries({ queryKey: ['prediction', product, experiment, prediction.id] });
      }
      setShowConfirmDialog(false);
    },
    onError: (error: any) => {
      console.error('Tag generation failed:', error);
      // Extract error message from response or error object
      const errorMessage = error.response?.data?.error || error.message || 'Unknown error';
      setSuccessMessage(`Tag generation failed: ${errorMessage}`);
      setShowSuccessMessage(true);
      setShowConfirmDialog(false);
    },
  });

  // Save-and-generate mutation (for dirty data)
  const saveAndGenerateTagsMutation = useMutation({
    mutationFn: () => tagGenerationService.saveAndGenerateTagsForLesson(
      product,
      experiment,
      prediction.id,
      workingPrediction.tag_prediction_modified || workingPrediction.tag_prediction_original || []
    ),
    onSuccess: (result) => {
      if (result.saveSuccess && result.generationResult.success) {
        // Update working prediction with new tag IDs
        const updatedPrediction = {
          ...workingPrediction,
          spine_tag_ids: result.generationResult.tagIds,
        };

        // Auto-save the changes to the database instead of marking as dirty
        _onUpdate(updatedPrediction);

        // Update local state and clear dirty flag since we auto-saved
        setWorkingPrediction(updatedPrediction);
        setIsDirty(false);

        // Show success message
        const mathCount = result.generationResult.mathExpressionTags?.length || 0;
        const message = `Saved and generated ${result.generationResult.tagIds.length} SPINE tag(s)${mathCount > 0 ? ` including ${mathCount} math expression(s)` : ''}`;
        setSuccessMessage(message);
        setShowSuccessMessage(true);

        // Invalidate prediction queries to ensure fresh data
        queryClient.invalidateQueries({ queryKey: ['prediction', product, experiment, prediction.id] });
      } else if (!result.saveSuccess) {
        setSuccessMessage('Failed to save changes before generating tags');
        setShowSuccessMessage(true);
      } else if (!result.generationResult.success) {
        setSuccessMessage(`Changes saved, but tag generation failed: ${result.generationResult.error || 'Unknown error'}`);
        setShowSuccessMessage(true);
      }
      setShowConfirmDialog(false);
    },
    onError: (error: any) => {
      console.error('Save and generate failed:', error);
      // Extract error message from response or error object
      const errorMessage = error.response?.data?.error || error.message || 'Unknown error';
      setSuccessMessage(`Save and generate failed: ${errorMessage}`);
      setShowSuccessMessage(true);
      setShowConfirmDialog(false);
    },
  });

  // Tag generation handlers
  const handleGenerateTagsClick = () => {
    setShowConfirmDialog(true);
  };

  const handleConfirmGeneration = () => {
    if (isDirty) {
      // Use save-and-generate for dirty data
      saveAndGenerateTagsMutation.mutate();
    } else {
      // Use regular generation for clean data
      generateTagsMutation.mutate();
    }
  };

  const handleCloseConfirmDialog = () => {
    setShowConfirmDialog(false);
  };

  // Handle external tag generation trigger
  useEffect(() => {
    if (triggerTagGeneration) {
      handleGenerateTagsClick();
    }
  }, [triggerTagGeneration]);

  // Notify parent about tag generation loading state changes
  useEffect(() => {
    if (onTagGenerationStateChange) {
      onTagGenerationStateChange(generateTagsMutation.isPending || saveAndGenerateTagsMutation.isPending);
    }
  }, [generateTagsMutation.isPending, saveAndGenerateTagsMutation.isPending, onTagGenerationStateChange]);

  // Notify parent about dirty state changes
  useEffect(() => {
    if (onDirtyStateChange) {
      onDirtyStateChange(isDirty);
    }
  }, [isDirty, onDirtyStateChange]);

  // Handle external save trigger using ref to avoid stale closure issues
  // Note: Don't set isDirty=false here - let the parent's onSuccess callback do it
  // after the save completes, to avoid the chart briefly showing old data
  useEffect(() => {
    if (triggerSave && isDirty) {
      // Save current working prediction changes using ref to get latest value
      _onUpdate(workingPredictionRef.current);
    }
  }, [triggerSave, isDirty]);

  // Extract tag candidates - memoized to prevent infinite re-renders
  const tagCandidates = useMemo(() => {
    let candidates: any[] = [];

    if (workingPrediction.tag_prediction_modified) {
      candidates = Array.isArray(workingPrediction.tag_prediction_modified)
        ? workingPrediction.tag_prediction_modified
        : [];
    } else if (workingPrediction.tag_prediction_original) {
      candidates = Array.isArray(workingPrediction.tag_prediction_original)
        ? workingPrediction.tag_prediction_original
        : [];
    }

    // Filter out null/undefined entries and entries without competency
    return candidates.filter((tag) => tag && tag.competency);
  }, [workingPrediction.tag_prediction_modified, workingPrediction.tag_prediction_original]);


  return (
    <Box>
      {/* Three-panel layout with resizable dividers */}
      <Box>
        <ResizablePanels
          height="750px"
          defaultLeftWidth={400}
        defaultRightWidth={350}
        minPanelWidth={250}
        leftPanel={
          <Paper sx={{ p: 2, height: '100%', overflow: 'auto' }}>
            <TagsPanel
              tagCandidates={tagCandidates}
              focusedTagIndex={focusedTagIndex}
              lessonId={prediction.id}
              onFocusedTagChange={handleTagFocus}
              onTagUpdate={handleTagUpdate}
              onAddTagCandidates={handleAddTagCandidates}
            />
          </Paper>
        }
        centerPanel={
          <Paper sx={{ p: 2, height: '100%', overflow: 'hidden' }}>
            <BipartiteChart
              tagCandidates={tagCandidates}
              mathExpressions={modifiedMathExpressions}
              focusedTagIndex={focusedTagIndex}
              onTagFocus={handleTagFocus}
              selectedGrades={selectedGrades}
              selectedDocuments={selectedDocuments}
              selectedJurisdictions={selectedJurisdictions}
              selectedStates={selectedStates}
              showNonSelectedTags={showNonSelectedTags}
              searchText={searchText}
              externalFocusedStandardId={focusedStandardId}
              onFocusedStandardsChange={(focusedData) => {
                setFocusedStandardId(focusedData.focusedStandardId);
                setFocusedTagStandardIds(focusedData.focusedTagStandardIds);
              }}
              isDirty={isDirty}
              onRevertChanges={handleRevert}
            />
          </Paper>
        }
        rightPanel={
          <Paper sx={{ p: 2, height: '100%', overflow: 'auto' }}>
            <StandardsPreview
              tagCandidates={tagCandidates}
              mathExpressions={modifiedMathExpressions}
              selectedGrades={selectedGrades}
              selectedDocuments={selectedDocuments}
              selectedJurisdictions={selectedJurisdictions}
              selectedStates={selectedStates}
              showNonSelectedTags={showNonSelectedTags}
              searchText={searchText}
              onGradesChange={setSelectedGrades}
              onDocumentsChange={setSelectedDocuments}
              onJurisdictionsChange={setSelectedJurisdictions}
              onStatesChange={setSelectedStates}
              onShowNonSelectedTagsChange={setShowNonSelectedTags}
              onSearchTextChange={setSearchText}
              onStandardClick={handleStandardClick}
              focusedStandardId={focusedStandardId}
              focusedTagStandardIds={focusedTagStandardIds}
            />
          </Paper>
        }
      />
      </Box>

      {/* Tag Generation Confirmation Dialog */}
      <TagGenerationConfirmDialog
        open={showConfirmDialog}
        onClose={handleCloseConfirmDialog}
        onConfirm={handleConfirmGeneration}
        prediction={workingPrediction}
        hasExistingTags={Boolean(workingPrediction.spine_tag_ids?.length)}
        isDirty={isDirty}
        loading={generateTagsMutation.isPending || saveAndGenerateTagsMutation.isPending}
      />

      {/* Success Snackbar */}
      <Snackbar
        open={showSuccessMessage}
        autoHideDuration={4000}
        onClose={() => setShowSuccessMessage(false)}
        message={successMessage}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
}
